import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, unref as _unref, createVNode as _createVNode, withKeys as _withKeys, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, toDisplayString as _toDisplayString, renderList as _renderList, Fragment as _Fragment, vModelText as _vModelText, withModifiers as _withModifiers, withDirectives as _withDirectives, createBlock as _createBlock, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "agent-threads" }
const _hoisted_2 = { class: "agent-threads__header" }
const _hoisted_3 = ["disabled"]
const _hoisted_4 = {
  key: 0,
  class: "agent-threads__search"
}
const _hoisted_5 = { class: "agent-threads__list" }
const _hoisted_6 = {
  key: 0,
  class: "agent-threads__state"
}
const _hoisted_7 = {
  key: 1,
  class: "agent-threads__state"
}
const _hoisted_8 = {
  key: 2,
  class: "agent-threads__state"
}
const _hoisted_9 = {
  key: 3,
  class: "agent-threads__state"
}
const _hoisted_10 = {
  key: 0,
  class: "agent-threads__edit"
}
const _hoisted_11 = ["onKeydown"]
const _hoisted_12 = ["disabled", "title", "onClick"]
const _hoisted_13 = { class: "agent-threads__title" }
const _hoisted_14 = { class: "agent-threads__title-text" }
const _hoisted_15 = {
  key: 1,
  class: "agent-threads__unread-dot",
  role: "img",
  "aria-label": "Unread"
}
const _hoisted_16 = {
  key: 0,
  class: "agent-threads__status agent-threads__status--reply"
}
const _hoisted_17 = {
  key: 1,
  class: "agent-threads__status agent-threads__status--enriching"
}
const _hoisted_18 = {
  key: 2,
  class: "agent-threads__status agent-threads__status--completed"
}
const _hoisted_19 = {
  key: 3,
  class: "agent-threads__date"
}
const _hoisted_20 = ["aria-label"]

import { computed, ref } from "vue";
import { Icon, Input, PopoverMenu, Spinner } from "@/components/ui";
import ActionConfirmationModal from "@/components/modal/ActionConfirmationModal.vue";
import { useAgentConversations } from "@/composables/agent/useAgentConversations";
import { useToast } from "@/composables/ui/useToast";
import type { AgentChatMode, AgentConversationSummary } from "@/dtos/Agent.dto";
import type { AgentEnrichmentStatus } from "@/composables/agent/useAgentEnrichmentTracker";

const untitled = "Untitled conversation";


export default /*@__PURE__*/_defineComponent({
  __name: 'AgentConversationList',
  props: {
    activeId: {},
    disabled: { type: Boolean },
    enrichmentStatus: {},
    mode: {}
  },
  emits: ["select", "new", "deleted"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

const toast = useToast();
const { list, remove, rename } = useAgentConversations();

const conversations = computed<AgentConversationSummary[]>(() => {
  const all = list.data.value ?? [];
  if (!props.mode) return all;
  return all.filter((conversation) => (conversation.mode ?? "prospecting") === props.mode);
});
const toDelete = ref<AgentConversationSummary | null>(null);

// Filters the loaded searches by title; every typed word has to appear, in any order.
const searchQuery = ref("");
const filteredConversations = computed(() => {
  const words = searchQuery.value.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (words.length === 0) return conversations.value;
  return conversations.value.filter((conversation) => {
    const title = (conversation.title || untitled).toLowerCase();
    return words.every((word) => title.includes(word));
  });
});

const formatDate = (iso: string): string => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  const sameDay = date.toDateString() === new Date().toDateString();
  return sameDay
    ? date.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })
    : date.toLocaleDateString([], { month: "short", day: "numeric" });
};

const menuItems = (conversation: AgentConversationSummary) => [
  {
    label: "Rename",
    icon: "mdi:pencil-outline",
    disabled: props.disabled,
    onClick: () => startRename(conversation),
  },
  {
    label: "Delete",
    icon: "mdi:delete-outline",
    danger: true,
    disabled: props.disabled,
    onClick: () => {
      toDelete.value = conversation;
    },
  },
];

// Inline rename: the title turns into a text field in place.
const editingId = ref<string | null>(null);
const editingTitle = ref("");

const startRename = (conversation: AgentConversationSummary) => {
  editingId.value = conversation.id;
  editingTitle.value = conversation.title || "";
};

const focusRenameInput = (el: unknown) => {
  if (el instanceof HTMLInputElement && document.activeElement !== el) {
    el.focus();
    el.select();
  }
};

const cancelRename = () => {
  editingId.value = null;
  editingTitle.value = "";
};

const saveRename = () => {
  const id = editingId.value;
  if (!id) return;
  const title = editingTitle.value.trim();
  const current = conversations.value.find((conversation) => conversation.id === id);
  // Closing the field first stops the blur that follows Enter from saving twice.
  cancelRename();
  if (!title || title === current?.title) return;
  rename.mutate(
    { id, title },
    { onError: () => toast.error("Couldn't rename this conversation. Please try again.") }
  );
};

const confirmDelete = () => {
  const target = toDelete.value;
  if (!target) return;
  remove.mutate(target.id, {
    onSuccess: (id) => {
      toDelete.value = null;
      emit("deleted", id);
    },
    onError: () => toast.error("Couldn't delete this conversation. Please try again."),
  });
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("aside", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _cache[8] || (_cache[8] = _createElementVNode("span", { class: "agent-threads__heading" }, "Conversations", -1)),
      _createElementVNode("button", {
        type: "button",
        class: "agent-threads__new",
        disabled: __props.disabled,
        onClick: _cache[0] || (_cache[0] = ($event: any) => (emit('new')))
      }, [
        _createVNode(_unref(Icon), { icon: "mdi:plus" }),
        _cache[7] || (_cache[7] = _createElementVNode("span", null, "New chat", -1))
      ], 8, _hoisted_3)
    ]),
    (conversations.value.length > 0)
      ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
          _createVNode(_unref(Input), {
            modelValue: searchQuery.value,
            "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((searchQuery).value = $event)),
            size: "sm",
            placeholder: "Search conversations",
            "leading-icon": "mdi:magnify",
            clearable: "",
            "aria-label": "Search past conversations",
            onKeydown: _cache[2] || (_cache[2] = _withKeys(($event: any) => (searchQuery.value = ''), ["esc"]))
          }, null, 8, ["modelValue"])
        ]))
      : _createCommentVNode("", true),
    _createElementVNode("div", _hoisted_5, [
      (_unref(list).isLoading.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
            _createVNode(_unref(Spinner))
          ]))
        : (_unref(list).isError.value)
          ? (_openBlock(), _createElementBlock("p", _hoisted_7, [
              _cache[9] || (_cache[9] = _createTextVNode(" Couldn't load your conversations. ", -1)),
              _createElementVNode("button", {
                type: "button",
                class: "agent-threads__retry",
                onClick: _cache[3] || (_cache[3] = ($event: any) => (_unref(list).refetch()))
              }, "Retry")
            ]))
          : (conversations.value.length === 0)
            ? (_openBlock(), _createElementBlock("p", _hoisted_8, " Your past conversations will show up here. "))
            : (filteredConversations.value.length === 0)
              ? (_openBlock(), _createElementBlock("p", _hoisted_9, [
                  _createTextVNode(" No conversations match “" + _toDisplayString(searchQuery.value.trim()) + "”. ", 1),
                  _createElementVNode("button", {
                    type: "button",
                    class: "agent-threads__retry",
                    onClick: _cache[4] || (_cache[4] = ($event: any) => (searchQuery.value = ''))
                  }, " Clear search ")
                ]))
              : _createCommentVNode("", true),
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(filteredConversations.value, (conversation) => {
        return (_openBlock(), _createElementBlock("div", {
          key: conversation.id,
          class: _normalizeClass(["agent-threads__item", {
          'agent-threads__item--active': conversation.id === __props.activeId,
          'agent-threads__item--disabled': __props.disabled,
          'agent-threads__item--editing': conversation.id === editingId.value,
          'agent-threads__item--unread': conversation.unread,
        }])
        }, [
          (conversation.id === editingId.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
                _withDirectives(_createElementVNode("input", {
                  ref_for: true,
                  ref: focusRenameInput,
                  "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((editingTitle).value = $event)),
                  class: "agent-threads__edit-input",
                  maxlength: "200",
                  "aria-label": "Search name",
                  onKeydown: [
                    _withKeys(_withModifiers(saveRename, ["prevent"]), ["enter"]),
                    _withKeys(_withModifiers(cancelRename, ["prevent"]), ["esc"])
                  ],
                  onBlur: saveRename
                }, null, 40, _hoisted_11), [
                  [_vModelText, editingTitle.value]
                ]),
                _cache[10] || (_cache[10] = _createElementVNode("span", { class: "agent-threads__edit-hint" }, "Enter to save · Esc to cancel", -1))
              ]))
            : (_openBlock(), _createElementBlock("button", {
                key: 1,
                type: "button",
                class: "agent-threads__open",
                disabled: __props.disabled,
                title: conversation.title || untitled,
                onClick: ($event: any) => (emit('select', conversation.id))
              }, [
                _createElementVNode("span", _hoisted_13, [
                  (conversation.mode === 'assistant')
                    ? (_openBlock(), _createBlock(_unref(Icon), {
                        key: 0,
                        icon: "mdi:creation",
                        class: "agent-threads__mode",
                        "aria-label": "CRM assistant"
                      }))
                    : _createCommentVNode("", true),
                  _createElementVNode("span", _hoisted_14, _toDisplayString(conversation.title || untitled), 1),
                  (conversation.unread)
                    ? (_openBlock(), _createElementBlock("span", _hoisted_15))
                    : _createCommentVNode("", true)
                ]),
                (conversation.unread)
                  ? (_openBlock(), _createElementBlock("span", _hoisted_16, [
                      _createVNode(_unref(Icon), { icon: "mdi:email-fast-outline" }),
                      _createTextVNode(" New reply · draft ready · " + _toDisplayString(formatDate(conversation.updatedAt)), 1)
                    ]))
                  : (__props.enrichmentStatus?.[conversation.id] === 'enriching')
                    ? (_openBlock(), _createElementBlock("span", _hoisted_17, [...(_cache[11] || (_cache[11] = [
                        _createElementVNode("span", {
                          class: "agent-threads__spinner",
                          "aria-hidden": "true"
                        }, null, -1),
                        _createTextVNode(" Enriching contacts… ", -1)
                      ]))]))
                    : (__props.enrichmentStatus?.[conversation.id] === 'completed')
                      ? (_openBlock(), _createElementBlock("span", _hoisted_18, [
                          _createVNode(_unref(Icon), { icon: "mdi:check-circle" }),
                          _cache[12] || (_cache[12] = _createTextVNode(" Enrichment completed ", -1))
                        ]))
                      : (_openBlock(), _createElementBlock("span", _hoisted_19, _toDisplayString(formatDate(conversation.updatedAt)), 1))
              ], 8, _hoisted_12)),
          (conversation.id !== editingId.value)
            ? (_openBlock(), _createElementBlock("div", {
                key: 2,
                class: "agent-threads__menu",
                "aria-label": `Options for ${conversation.title || untitled}`
              }, [
                _createVNode(_unref(PopoverMenu), {
                  items: menuItems(conversation),
                  "trigger-icon": "mdi:dots-vertical",
                  placement: "bottom-right"
                }, null, 8, ["items"])
              ], 8, _hoisted_20))
            : _createCommentVNode("", true)
        ], 2))
      }), 128))
    ]),
    _createVNode(ActionConfirmationModal, {
      show: !!toDelete.value,
      title: "Delete conversation",
      message: `Delete "${toDelete.value?.title || untitled}"? Contacts already imported from it are kept.`,
      "confirm-text": "Delete",
      "cancel-text": "Cancel",
      "is-loading": _unref(remove).isPending.value,
      onConfirm: confirmDelete,
      onClose: _cache[6] || (_cache[6] = ($event: any) => (toDelete.value = null))
    }, null, 8, ["show", "message", "is-loading"])
  ]))
}
}

})